import type { Object3D } from 'three';
import { CanvasTexture, Mesh, MeshBasicMaterial, PlaneGeometry, SRGBColorSpace } from 'three';

import type { ApplianceDragState, DropIndicatorContent, DropIndicatorIcon } from '../types';

/** `userData.type` of the floor label — never a hit target. */
export const DROP_INDICATOR_TYPE = 'drag-indicator';

const CANVAS_W = 1024;
const CANVAS_H = 256;
const PILL_H = 200;
const PAD_X = 56;
const ICON_BOX = 120;
const ICON_GAP = 28;
const FONT_PX = 92;
const MIN_FONT_PX = 52;
const FONT_FAMILY = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/**
 * The label printed on the floor in front of the lifted column: a plane lying
 * flat, carrying a canvas-drawn pill (icon + text) that says what a drop
 * would do here. Parented to the island it points at, so island-local x and
 * the panel's outward side place it.
 */
export interface DropIndicator {
	mesh: Mesh<PlaneGeometry, MeshBasicMaterial>;
	canvas: HTMLCanvasElement;
	texture: CanvasTexture;
	content: DropIndicatorContent | null;
}

const font = (px: number): string => `600 ${px}px ${FONT_FAMILY}`;

const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void => {
	const radius = Math.min(r, w / 2, h / 2);
	ctx.beginPath();
	ctx.moveTo(x + radius, y);
	ctx.lineTo(x + w - radius, y);
	ctx.arcTo(x + w, y, x + w, y + radius, radius);
	ctx.lineTo(x + w, y + h - radius);
	ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
	ctx.lineTo(x + radius, y + h);
	ctx.arcTo(x, y + h, x, y + h - radius, radius);
	ctx.lineTo(x, y + radius);
	ctx.arcTo(x, y, x + radius, y, radius);
	ctx.closePath();
};

const arrowHead = (ctx: CanvasRenderingContext2D, tipX: number, tipY: number, direction: 1 | -1, size: number): void => {
	ctx.beginPath();
	ctx.moveTo(tipX - (direction * size), tipY - size);
	ctx.lineTo(tipX, tipY);
	ctx.lineTo(tipX - (direction * size), tipY + size);
	ctx.stroke();
};

/** Line-art glyph in a square box: what the drop does, readable before the text is. */
const drawIcon = (ctx: CanvasRenderingContext2D, icon: DropIndicatorIcon, x: number, y: number, size: number): void => {
	const cx = x + (size / 2);
	const cy = y + (size / 2);
	ctx.lineCap = 'round';
	ctx.lineJoin = 'round';
	ctx.lineWidth = size * 0.1;
	ctx.strokeStyle = icon === 'blocked' ? '#ff8a8a' : '#ffffff';
	const head = size * 0.16;
	switch (icon) {
		case 'move': {
			ctx.beginPath();
			ctx.moveTo(x + (size * 0.12), cy);
			ctx.lineTo(x + (size * 0.88), cy);
			ctx.stroke();
			arrowHead(ctx, x + (size * 0.88), cy, 1, head);
			arrowHead(ctx, x + (size * 0.12), cy, -1, head);
			break;
		}
		case 'swap': {
			const top = cy - (size * 0.2);
			const bottom = cy + (size * 0.2);
			ctx.beginPath();
			ctx.moveTo(x + (size * 0.1), top);
			ctx.lineTo(x + (size * 0.9), top);
			ctx.stroke();
			arrowHead(ctx, x + (size * 0.9), top, 1, head);
			ctx.beginPath();
			ctx.moveTo(x + (size * 0.9), bottom);
			ctx.lineTo(x + (size * 0.1), bottom);
			ctx.stroke();
			arrowHead(ctx, x + (size * 0.1), bottom, -1, head);
			break;
		}
		case 'grow': {
			ctx.beginPath();
			ctx.moveTo(x + (size * 0.16), cy);
			ctx.lineTo(x + (size * 0.84), cy);
			ctx.stroke();
			arrowHead(ctx, x + (size * 0.84), cy, 1, head * 0.9);
			arrowHead(ctx, x + (size * 0.16), cy, -1, head * 0.9);
			ctx.beginPath();
			ctx.moveTo(x + (size * 0.06), cy - (size * 0.3));
			ctx.lineTo(x + (size * 0.06), cy + (size * 0.3));
			ctx.moveTo(x + (size * 0.94), cy - (size * 0.3));
			ctx.lineTo(x + (size * 0.94), cy + (size * 0.3));
			ctx.stroke();
			break;
		}
		case 'blocked': {
			const r = size * 0.36;
			ctx.beginPath();
			ctx.arc(cx, cy, r, 0, Math.PI * 2);
			ctx.stroke();
			ctx.beginPath();
			ctx.moveTo(cx - (r * 0.7), cy - (r * 0.7));
			ctx.lineTo(cx + (r * 0.7), cy + (r * 0.7));
			ctx.stroke();
			break;
		}
		default:
			break;
	}
};

const DETAIL_FONT_PX = 50;
const DETAIL_MIN_FONT_PX = 34;
const DETAIL_COLORS = { warn: '#ffd36b', error: '#ff8a8a', ok: '#8be28b' } as const;

/** Shrink the font until the text fits `maxW`; returns the font size used and the measured width. */
const fitText = (ctx: CanvasRenderingContext2D, text: string, startPx: number, minPx: number, maxW: number): { px: number; width: number } => {
	let px = startPx;
	ctx.font = font(px);
	let width = ctx.measureText(text).width;
	while (width > maxW && px > minPx) {
		px -= 4;
		ctx.font = font(px);
		width = ctx.measureText(text).width;
	}
	return { px, width: Math.min(width, maxW) };
};

const draw = (indicator: DropIndicator, content: DropIndicatorContent): void => {
	const ctx = indicator.canvas.getContext('2d');
	if (!ctx) return;
	ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

	const icon: DropIndicatorIcon = content.icon ?? 'none';
	const hasIcon = icon !== 'none';
	const iconSpan = hasIcon ? ICON_BOX + ICON_GAP : 0;
	const detail = content.detail ?? null;
	const maxTextW = CANVAS_W - 8 - (2 * PAD_X) - iconSpan;

	const main = fitText(ctx, content.text, detail ? FONT_PX - 8 : FONT_PX, MIN_FONT_PX, maxTextW);
	const second = detail ? fitText(ctx, detail.text, DETAIL_FONT_PX, DETAIL_MIN_FONT_PX, maxTextW) : null;

	// one line sits mid-canvas; with a detail line the pill grows to nearly the full height
	const pillH = detail ? CANVAS_H - 16 : PILL_H;
	const pillW = (2 * PAD_X) + iconSpan + Math.max(main.width, second?.width ?? 0);
	const x0 = (CANVAS_W - pillW) / 2;
	const y0 = (CANVAS_H - pillH) / 2;
	roundRect(ctx, x0, y0, pillW, pillH, Math.min(pillH / 2, PILL_H / 2));
	ctx.fillStyle = icon === 'blocked' ? 'rgba(96, 22, 22, 0.88)' : 'rgba(18, 22, 30, 0.86)';
	ctx.fill();
	ctx.lineWidth = 4;
	ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
	ctx.stroke();

	const mainY = detail ? y0 + (pillH * 0.34) : (CANVAS_H / 2) + 4;
	let cursorX = x0 + PAD_X;
	if (hasIcon) {
		drawIcon(ctx, icon, cursorX, mainY - (ICON_BOX / 2), ICON_BOX);
		cursorX += iconSpan;
	}
	ctx.fillStyle = '#ffffff';
	ctx.textAlign = 'left';
	ctx.textBaseline = 'middle';
	ctx.font = font(main.px);
	ctx.fillText(content.text, cursorX, mainY, maxTextW);

	if (detail && second) {
		ctx.fillStyle = DETAIL_COLORS[detail.tone] ?? DETAIL_COLORS.warn;
		ctx.font = font(second.px);
		ctx.fillText(detail.text, cursorX, y0 + (pillH * 0.73), maxTextW);
	}

	indicator.texture.needsUpdate = true;
};

/** A hidden, empty label; null where there is no DOM (tests, SSR). */
export const createDropIndicatorCfn = (widthIn: number, heightIn: number): DropIndicator | null => {
	if (typeof document === 'undefined') return null;
	const canvas = document.createElement('canvas');
	canvas.width = CANVAS_W;
	canvas.height = CANVAS_H;
	const texture = new CanvasTexture(canvas);
	texture.colorSpace = SRGBColorSpace;
	texture.anisotropy = 4;
	const material = new MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false, toneMapped: false, opacity: 1 });
	const mesh = new Mesh(new PlaneGeometry(widthIn, heightIn), material);
	mesh.name = 'appliance-drag-indicator';
	mesh.userData = { type: DROP_INDICATOR_TYPE, id: 'drag-indicator' };
	mesh.renderOrder = 20;
	mesh.visible = false;
	return { mesh, canvas, texture, content: null };
};

/**
 * Show `content` (redrawn only when the text or icon changed) or hide the
 * label with null. Returns true when the label just became visible.
 */
export const setDropIndicatorContentCfn = (indicator: DropIndicator, content: DropIndicatorContent | null): boolean => {
	if (!content) {
		indicator.mesh.visible = false;
		indicator.content = null;
		return false;
	}
	const previous = indicator.content;
	const sameDetail = (previous?.detail?.text ?? null) === (content.detail?.text ?? null) && (previous?.detail?.tone ?? null) === (content.detail?.tone ?? null);
	if (!previous || previous.text !== content.text || (previous.icon ?? 'none') !== (content.icon ?? 'none') || !sameDetail) {
		draw(indicator, content);
		indicator.content = { text: content.text, icon: content.icon ?? 'none', detail: content.detail ?? null };
		indicator.mesh.userData.label = content.text;
		indicator.mesh.userData.icon = content.icon ?? 'none';
		indicator.mesh.userData.detail = content.detail?.text ?? null;
	}
	const shown = !indicator.mesh.visible;
	indicator.mesh.visible = true;
	return shown;
};

/** Lay the label on the island's floor, centred on column x, `offsetIn` out from the front face, readable from in front. */
export const placeDropIndicatorCfn = (
	indicator: DropIndicator,
	island: { group: Object3D; frontZ: number; outwardSign: 1 | -1; floorY: number },
	x: number,
	offsetIn: number,
	liftIn: number
): void => {
	const { mesh } = indicator;
	if (mesh.parent !== island.group) island.group.add(mesh);
	mesh.position.set(x, island.floorY + liftIn, island.frontZ + (island.outwardSign * offsetIn));
	// flat on the floor; the text's top points at the island, so it reads from the front
	mesh.rotation.set(-Math.PI / 2, 0, island.outwardSign === 1 ? 0 : Math.PI);
};

export const disposeDropIndicatorCfn = (indicator: DropIndicator): void => {
	indicator.mesh.parent?.remove(indicator.mesh);
	indicator.mesh.geometry.dispose();
	indicator.mesh.material.dispose();
	indicator.texture.dispose();
};

const formatFeet = (inches: number): string => {
	const feet = Math.round((inches / 12) * 10) / 10;
	return `${Number.isInteger(feet) ? feet : feet.toFixed(1)} ft`;
};

/** The validation line under the words: the first issue the drop would introduce, else what it clears. */
export const defaultIssueDetailCfn = (state: ApplianceDragState): DropIndicatorContent['detail'] => {
	const fresh = (state.issues ?? []).filter((issue) => issue.isNew);
	if (fresh.length > 0) {
		const first = fresh[0];
		return { text: first.message, tone: first.severity === 'warn' || first.severity === 'info' ? 'warn' : 'error' };
	}
	const resolved = state.resolvedIssues ?? 0;
	if (resolved > 0) return { text: `Clears ${resolved} warning${resolved === 1 ? '' : 's'}`, tone: 'ok' };
	return null;
};

/** The label without the app's names: what a drop does, in the gesture's own words. */
export const defaultDropIndicatorCfn = (state: ApplianceDragState): DropIndicatorContent | null => {
	if (state.phase !== 'dragging' || !state.mode) return null;
	if (state.mode === 'refused') {
		switch (state.refusedReason) {
			case 'island-busy':
				return { text: 'Still updating', icon: 'blocked' };
			case 'exceeds-max-width':
				return { text: 'Too wide', icon: 'blocked' };
			default:
				return { text: 'No room', icon: 'blocked' };
		}
	}
	const detail = defaultIssueDetailCfn(state);
	const withDetail = (content: DropIndicatorContent): DropIndicatorContent => (detail ? { ...content, detail } : content);
	if (state.mode === 'fine-tune') return withDetail({ text: 'Place here', icon: 'move' });
	if (state.mode === 'swap') return withDetail({ text: 'Swap places', icon: 'swap' });
	if (state.willResize) return withDetail({ text: `Grows to ${formatFeet(state.willResize.toWidthIn)}`, icon: 'grow' });
	return withDetail({ text: 'Move here', icon: 'move' });
};
